class RecentlyViewedUtil {
  static storageKey = 'recently-viewed-products';
  static maxProducts = 8;

  static getProducts() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  static addProduct(productId, productUrl, productImage) {
    if (!productId) return;

    const currentList = this.getProducts();
    const existingProducts = currentList.filter(product => product.product_id !== productId);

    existingProducts.unshift({
      product_id: productId,
      url: productUrl,
      image: productImage,
      timestamp: Date.now()
    });

    const limitedList = existingProducts.slice(0, this.maxProducts);

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(limitedList));
    } catch (e) {
      console.warn('Failed to save recently viewed products:', e);
    }
  }

  static getProductIds() {
    return this.getProducts().map(product => product.product_id);
  }
}

class RecentlyViewed extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset?.sectionId;
    if (!this.sectionId) {
      console.warn('Recently viewed: missing section ID');
      return;
    }
    this.init();
  }

  init() {
    const productIds = RecentlyViewedUtil.getProductIds();

    if (productIds.length === 0) {
      this.hide();
      return;
    }

    this.updateContent(productIds);
  }

  async updateContent(productIds) {
    if (!productIds?.length) return;

    const idsQuery = productIds.map(id => `id:${id}`).join(' OR ');
    const url = `${window.routes.search_url}?section_id=recently-viewed&q=${encodeURIComponent(idsQuery)}&type=product`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector(`[data-id="${this.sectionId}"]`) || doc.querySelector('#recently-viewed-section');

      if (!newContent) {
        this.hide();
        return;
      }

      const grid = this.findGrid(newContent);
      if (!grid || !grid.children.length) {
        this.hide();
        return;
      }

      this.updateGrid(grid);
      this.show();
      this.initializeSliderIfNeeded();

    } catch (error) {
      console.warn('Failed to load recently viewed products:', error);
      this.hide();
    }
  }

  findGrid(container) {
    return container.querySelector('.product-grid') || container.querySelector('[id*="recently-viewed-grid"]');
  }

  updateGrid(fetchedGrid) {
    const emptyMessage = this.querySelector('.recently-viewed__empty');
    if (emptyMessage) emptyMessage.remove();

    const currentGrid = this.querySelector('.product-grid') || this.querySelector('[id*="recently-viewed-grid"]');
    if (!currentGrid) return;

    currentGrid.innerHTML = fetchedGrid.innerHTML;

    // Copy attributes from fetched grid
    Array.from(fetchedGrid.attributes).forEach(attr => {
      if (attr.name !== 'id') {
        currentGrid.setAttribute(attr.name, attr.value);
      }
    });
  }

  initializeSliderIfNeeded() {
    const gridWithSlider = this.querySelector('[data-slider-url]');
    if (!gridWithSlider) return;

    // Get column count and product count
    const productCount = gridWithSlider.children.length;
    const columnCount = this.getColumnCount(gridWithSlider);

    // Only initialize slider if products exceed columns
    if (productCount > columnCount) {
      const sliderUrl = gridWithSlider.getAttribute('data-slider-url');
      this.loadSliderScript(sliderUrl, gridWithSlider);
    }
  }

  getColumnCount(gridElement) {
    // Extract column count from grid classes
    const className = gridElement.className;

    // Check for desktop columns first (grid--4-col-desktop)
    const desktopMatch = className.match(/grid--(\d+)-col-desktop/);
    if (desktopMatch) {
      return parseInt(desktopMatch[1]);
    }

    // Fallback to mobile columns (grid--2-col)
    const mobileMatch = className.match(/grid--(\d+)-col(?!\w)/);
    if (mobileMatch) {
      return parseInt(mobileMatch[1]);
    }

    // Default fallback
    return 4;
  }

  show() {
    this.classList.remove('recently-viewed--hidden');
  }

  hide() {
    this.classList.add('recently-viewed--hidden');
  }

  async loadSliderScript(sliderUrl, gridElement) {
    try {
      // Load CSS if needed
      if (!document.querySelector('link[href*="flickity.min.css"]')) {
        const cssLink = document.createElement('link');
        cssLink.rel = 'stylesheet';
        cssLink.href = sliderUrl.replace('.js', '.css');
        document.head.appendChild(cssLink);
      }

      // Load JS if needed
      if (typeof Flickity === 'undefined') {
        await this.loadScript(sliderUrl);
      }

      this.initializeSlider(gridElement);
    } catch (error) {
      console.error('Failed to load slider:', error);
    }
  }

  loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  initializeSlider(gridElement) {
    if (!gridElement || typeof Flickity === 'undefined') return;

    // Destroy existing instance
    if (gridElement.flickityInstance) {
      gridElement.flickityInstance.destroy();
    }

    try {
      const flickityOptions = JSON.parse(gridElement.getAttribute('data-flickity') || '{}');
      gridElement.flickityInstance = new Flickity(gridElement, flickityOptions);

      // Force resize after initialization
      setTimeout(() => {
        gridElement.flickityInstance?.resize();
      }, 100);
    } catch (error) {
      console.error('Flickity initialization failed:', error);
    }
  }

  disconnectedCallback() {
    // Cleanup Flickity instances
    const sliderElements = this.querySelectorAll('[data-slider-url]');
    sliderElements.forEach(element => {
      if (element.flickityInstance) {
        element.flickityInstance.destroy();
      }
    });
  }
}

customElements.define('recently-viewed', RecentlyViewed);

function trackRecentlyViewed() {
  const productData = window.ShopifyAnalytics?.meta?.product;
  if (!productData?.id) return;

  RecentlyViewedUtil.addProduct(
    productData.id,
    window.location.pathname,
    productData.featured_image || ''
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', trackRecentlyViewed);
} else {
  trackRecentlyViewed();
}